import AWS from 'aws-sdk';

// Set default region
AWS.config.region = 'us-gov-west-1';

// Initialize AWS configuration
let awsConfigured = false;

// Global error handler for expired tokens
let onTokenExpired = null;

export const setTokenExpiredHandler = (handler) => {
  onTokenExpired = handler;
};

// Intercept AWS errors globally
const originalSend = AWS.Request.prototype.send;
AWS.Request.prototype.send = function(callback) {
  const wrappedCallback = function(err, data) {
    if (err && (err.code === 'ExpiredTokenException' || err.code === 'ExpiredToken')) {
      console.error('AWS Token expired:', err);
      if (onTokenExpired) {
        onTokenExpired();
      }
    }
    if (callback) {
      callback(err, data);
    }
  };
  
  return originalSend.call(this, wrappedCallback);
};

// Function to configure AWS SDK with credentials from localStorage
export const configureAWS = (credentials) => {
  if (!credentials) {
    // Try to load from localStorage
    const storedCreds = localStorage.getItem('awsCredentials');
    if (storedCreds) {
      credentials = JSON.parse(storedCreds);
    }
  }

  if (credentials) {
    const awsConfig = {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: credentials.region || 'us-gov-west-1',
      s3ForcePathStyle: false,
      signatureVersion: 'v4'
    };

    console.log('AWS Config loaded:', {
      accessKeyId: awsConfig.accessKeyId ? awsConfig.accessKeyId.substring(0, 10) + '...' : 'MISSING',
      secretAccessKey: awsConfig.secretAccessKey ? 'Present' : 'MISSING',
      sessionToken: awsConfig.sessionToken ? 'Present' : 'MISSING',
      region: awsConfig.region,
      expiration: credentials.expiration
    });

    AWS.config.update(awsConfig);
    awsConfigured = true;
    
    // Reset clients to pick up new credentials
    s3Client = null;
    dynamodbClient = null;
  } else {
    console.warn('No AWS credentials available');
  }
};

// Function to check if credentials are expired
export const areCredentialsExpired = () => {
  const storedCreds = localStorage.getItem('awsCredentials');
  if (!storedCreds) return true;
  
  const credentials = JSON.parse(storedCreds);
  if (!credentials.expiration) return false;
  
  const expirationTime = new Date(credentials.expiration).getTime();
  const currentTime = new Date().getTime();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
  
  return (expirationTime - currentTime) < bufferTime;
};

// Function to refresh credentials
export const refreshCredentials = async () => {
  const tokens = localStorage.getItem('cognitoTokens');
  if (!tokens) {
    throw new Error('No refresh token available');
  }

  const { refreshToken } = JSON.parse(tokens);
  const apiEndpoint = process.env.REACT_APP_AUTH_API_ENDPOINT;

  if (!apiEndpoint) {
    throw new Error('Auth API endpoint not configured');
  }

  console.log('Attempting to refresh credentials...');

  const response = await fetch(`${apiEndpoint}/auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      action: 'refresh',
      refreshToken: refreshToken
    })
  });

  const data = await response.json();

  if (!response.ok) {
    console.error('Refresh failed:', data);
    throw new Error(data.error || 'Failed to refresh credentials');
  }

  if (!data.credentials || !data.tokens) {
    throw new Error('Invalid refresh response - missing credentials or tokens');
  }

  // Update stored credentials and tokens
  localStorage.setItem('awsCredentials', JSON.stringify(data.credentials));
  localStorage.setItem('cognitoTokens', JSON.stringify(data.tokens));

  // Reconfigure AWS SDK
  configureAWS(data.credentials);

  console.log('Credentials refreshed successfully');
  return data.credentials;
};

// Don't auto-configure on import - let App.js handle it after checking localStorage
// This prevents race conditions where components try to use AWS before credentials are loaded

// Lazy-loaded clients - these are created on-demand after credentials are configured
let s3Client = null;
let dynamodbClient = null;

// Getter functions that create clients on first access
export const getS3 = () => {
  if (!s3Client) {
    s3Client = new AWS.S3({
      region: 'us-gov-west-1',
      s3ForcePathStyle: false,
      signatureVersion: 'v4'
    });
  }
  return s3Client;
};

export const getDynamoDB = () => {
  if (!dynamodbClient) {
    dynamodbClient = new AWS.DynamoDB.DocumentClient({
      region: 'us-gov-west-1'
    });
  }
  return dynamodbClient;
};

// Export as properties for backward compatibility
export const s3 = new Proxy({}, {
  get: (target, prop) => {
    return getS3()[prop];
  }
});

export const dynamodb = new Proxy({}, {
  get: (target, prop) => {
    return getDynamoDB()[prop];
  }
});

export { AWS };

export const config = {
  bucketName: 'form-processing-bucket-485102866935-us-gov-west-1',
  templatesTable: 'form-processing-backend-form-templates',
  submissionsTable: 'form-processing-backend-form-submissions',
  businessRulesTable: 'business-rules-system-business-rules',
  validationDataTable: 'business-rules-system-validation-data',
  ruleViolationsTable: 'business-rules-system-rule-violations',
  region: 'us-gov-west-1'
};